import type { VoiceEntry } from './index';

/** A ticket the voice pipeline suggests — the agent picks; nothing is auto-selected. */
export interface VoiceCandidateTicket {
  id: string;
  title: string;
  status: string;
  organization_id: string;
  organization_name: string;
  assigned_to_me: boolean;
}

/**
 * DRAFT work log from a voice recording (POST /voice/upload). Never persisted
 * as a work log by the server; confirmed via POST /work-logs with
 * `voice_entry_id`.
 */
export interface VoiceWorkLogProposal {
  voice_entry_id: string;
  candidate_tickets: VoiceCandidateTicket[];
  ticket_reference: string | null;
  organization_name: string | null;
  duration_minutes: number | null;
  /** Always null — the agent sets the real times. */
  start_time: null;
  end_time: null;
  /** What the speaker said about times, verbatim (hint only). */
  stated_start_time: string | null;
  stated_end_time: string | null;
  summary: string;
  confidence: number;
  needs_review: boolean;
}

export interface VoiceUploadResult {
  voice_entry: VoiceEntry;
  /** Always null since decisions 126/127 (kept for backward compatibility). */
  work_log: null;
  requires_confirmation?: boolean;
  proposal?: VoiceWorkLogProposal;
  message: string;
}

/** Pre-fill for the manual work-log form. */
export interface WorkLogDraftInitial {
  ticket?: { id: string; title: string; organization_name?: string };
  summary?: string;
  durationMinutes?: number | null;
  statedStart?: string | null;
  statedEnd?: string | null;
  voiceEntryId?: string;
}

/** Draft from a stored voice entry (e.g. a recording confirmed later). */
export function draftFromVoiceEntry(entry: VoiceEntry): WorkLogDraftInitial {
  const p = (entry.parsed_payload ?? {}) as Partial<{
    summary: string;
    duration_minutes: number;
    start_time: string;
    end_time: string;
  }>;
  const duration = typeof p.duration_minutes === 'number' && p.duration_minutes > 0 ? Math.round(p.duration_minutes) : null;
  return {
    summary: p.summary ?? '',
    durationMinutes: duration,
    statedStart: p.start_time ?? null,
    statedEnd: p.end_time ?? null,
    voiceEntryId: entry.id,
  };
}
